/**
 * Shared subscription configuration. Defines plan/product types and default
 * data; persisted in localStorage for admin overrides.
 */

export interface Plan {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  monthlyPrice: number;
  annualPrice: number;
  freemiumMonths?: number;
  features: string[];
  limits: {
    pets?: number;
    storage?: number;
    animals?: number;
    appointments?: number;
    teamMembers?: number;
  };
}

export interface ProductConfig {
  id: string;
  name: string;
  plans: Plan[];
  billingEnabled?: boolean;
}

const STORAGE_KEY = 'petso_subscription_products';

const defaultPetsoPlans: Plan[] = [
  {
    id: 'petso-free',
    name: 'Free',
    status: 'active',
    monthlyPrice: 0,
    annualPrice: 0,
    freemiumMonths: 0,
    features: ['Basic pet profile', 'Health records', 'Up to 3 pets'],
    limits: { pets: 3, storage: 512, appointments: 10, teamMembers: 0 },
  },
  {
    id: 'petso-pro',
    name: 'Pro',
    status: 'active',
    monthlyPrice: 9.99,
    annualPrice: 95.99,
    freemiumMonths: 0,
    features: [
      'Unlimited pets',
      'Advanced health',
      'Priority support',
      'Rewards',
    ],
    limits: { pets: 999, storage: 10240, appointments: 999, teamMembers: 1 },
  },
];

const defaultPetsoProPlans: Plan[] = [
  {
    id: 'petso-pro-free',
    name: 'Free',
    status: 'active',
    monthlyPrice: 0,
    annualPrice: 0,
    freemiumMonths: 0,
    features: ['Basic listing', 'Up to 5 appointments/month'],
    limits: { pets: 0, appointments: 5, teamMembers: 0 },
  },
  {
    id: 'petso-pro-growth',
    name: 'Growth',
    status: 'active',
    monthlyPrice: 29,
    annualPrice: 278,
    freemiumMonths: 0,
    features: ['Unlimited appointments', 'Calendar sync', 'Reviews'],
    limits: { appointments: 999, teamMembers: 3 },
  },
];

export const INITIAL_SUBSCRIPTION_PRODUCTS: ProductConfig[] = [
  {
    id: 'petso',
    name: 'Petso',
    plans: defaultPetsoPlans,
    billingEnabled: false,
  },
  {
    id: 'petso-pro',
    name: 'Petso Pro',
    plans: defaultPetsoProPlans,
    billingEnabled: false,
  },
];

export function loadSubscriptionProductsFromStorage(): ProductConfig[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed as ProductConfig[];
  } catch {
    return null;
  }
}
